import React, { FormEvent, useCallback, useState } from "react";
import { connect } from "react-redux";

import { LocalDate } from "js-joda";
import { bindActionCreators, Dispatch } from "redux";

import { Dropdown, FilterItem, FilterItemField, FilterSection, FilterTextInput, DateInput } from "@plex/react-components";

import { arrayHelpers } from "infrastructure/helpers";
import { commonMessages, ILocalizable } from "infrastructure/localization";
import { useLocalizedValues, useLocalizer } from "ui/hooks";
import { releaseMessages } from "ui/screens/release/release.localization";

import { releasesManagerActions } from "../ReleasesManager.actions";
import { IReleasesManagerFilterData, IReleasesManagerState } from "../ReleasesManager.state";

interface IReleasesManagerFilterProps {
  statuses: ILocalizable[];
  search: (filterData?: IReleasesManagerFilterData) => void;
}

const ReleasesManagerFilter: React.FC<IReleasesManagerFilterProps> = ({ statuses, search }) => {
  const localize = useLocalizer();

  const localizedValues = useLocalizedValues(() => {
    return {
      status: releaseMessages.filter("status"),
      name: releaseMessages.filter("releaseName"),
      propertyStatus: releaseMessages.property("status"),
      all: commonMessages.property("all"),
      createdDate: releaseMessages.filter("createdDate"),
      releaseDate: releaseMessages.filter("releaseDate")
    };
  });
  const [state, setState] = useState<IReleasesManagerFilterData>({
    filterName: "",
    selectedStatus: undefined,
    createdDate: "",
    releaseDate: ""
  });
  const [pinned, setPinned] = useState(false);

  const onStatusSelected = useCallback(
    (items: ILocalizable[]) => {
      setState({ ...state, selectedStatus: items && items.length ? items[0] : undefined });
    },
    [state]
  );

  const onFilterNameChanged = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      setState({ ...state, filterName: event.currentTarget.value });
    },
    [state]
  );

  const onCreatedDateChanged = useCallback(
    (value: LocalDate | null) => {
      setState({ ...state, createdDate: value ? value.toString() : undefined });
    },
    [state]
  );

  const onReleaseDateChanged = useCallback(
    (value: LocalDate | null) => {
      setState({ ...state, releaseDate: value ? value.toString() : undefined });
    },
    [state]
  );

  const onSearch = useCallback(() => {
    search({ selectedStatus: state.selectedStatus, filterName: state.filterName, createdDate: state.createdDate, releaseDate: state.releaseDate });
  }, [search, state.filterName, state.selectedStatus, state.createdDate, state.releaseDate]);

  return (
    <FilterSection onSearch={onSearch} pinned={pinned} onPin={setPinned}>
      <FilterItem labelText={localizedValues.name} primaryFieldId="name">
        <FilterItemField shouldCollapse={() => !state.filterName}>
          <FilterTextInput id="name" value={state.filterName || ""} onChange={onFilterNameChanged} />
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.status} primaryFieldId="status">
        <FilterItemField value={state.selectedStatus}>
          <Dropdown
            id="status"
            items={statuses}
            includeEmpty
            placeholder={localizedValues.all}
            keySelector={item => item.messageId.value}
            displaySelector={item => localize(item.messageId)}
            selected={arrayHelpers.createFrom(state.selectedStatus)}
            onSelectionChanged={items => onStatusSelected(items)}
          />
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.createdDate} primaryFieldId="createdDate">
        <FilterItemField value={state.createdDate}>
          <DateInput id="createdDate" value={state.createdDate ? LocalDate.parse(state.createdDate) : undefined} onValueChange={value => onCreatedDateChanged(value)} />
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.releaseDate} primaryFieldId="releaseDate">
        <FilterItemField value={state.releaseDate}>
          <DateInput id="releaseDate" value={state.releaseDate ? LocalDate.parse(state.releaseDate) : undefined} onValueChange={value => onReleaseDateChanged(value)} />
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { releasesManager: IReleasesManagerState }) => {
    const filter = state.releasesManager.filter;
    return {
      statuses: filter.statuses
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        search: releasesManagerActions.search
      },
      dispatch
    );
  }
)(ReleasesManagerFilter);
